import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import { Alert } from '../services/AlertsService';
import { Dashboard } from '../../dashboards/services/DashboardsService';

interface HistoricalAlertsTableProps {
  alerts: Alert[];
  dashboards: Dashboard[];
  loading: boolean;
}

const HistoricalAlertsTable: React.FC<HistoricalAlertsTableProps> = ({
  alerts,
  dashboards,
  loading
}) => {
  // Helper function to get dashboard name by ID
  const getDashboardName = (id: number): string => {
    return dashboards.find(d => d.id === id)?.name || `Dashboard #${id}`;
  };

  // Helper function to render location
  const renderLocation = (alert: Alert) => {
    const locations: string[] = [];
    
    if (alert.site) locations.push('Site');
    if (alert.hub) locations.push('Hub');
    
    if (alert.dashboards && alert.dashboards.length > 0) {
      if (alert.dashboards.length === 1) {
        locations.push(getDashboardName(alert.dashboards[0]));
      } else {
        // Group dashboards by category for tooltip
        const dashboardsByCategory = alert.dashboards.reduce((acc, id) => {
          const dashboard = dashboards.find(d => d.id === id);
          if (dashboard) {
            const categoryName = dashboard.category?.name || 'Uncategorized';
            if (!acc[categoryName]) {
              acc[categoryName] = [];
            }
            acc[categoryName].push(dashboard.name);
          }
          return acc;
        }, {} as Record<string, string[]>);

        const tooltipContent = (
          <Box>
            {Object.entries(dashboardsByCategory).map(([category, names]) => (
              <Box key={category} sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {category}
                </Typography>
                {names.map(name => (
                  <Typography key={name} variant="body2" sx={{ pl: 1 }}>
                    • {name}
                  </Typography>
                ))}
              </Box>
            ))}
          </Box>
        );

        return (
          <Box>
            {locations.join(', ')}
            {locations.length > 0 && ', '}
            <Tooltip title={tooltipContent} arrow>
              <span style={{ textDecoration: 'underline', cursor: 'help' }}>
                Dashboards ({alert.dashboards.length})
              </span>
            </Tooltip>
          </Box>
        );
      }
    }
    
    return locations.join(', ') || 'None';
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Sort alerts by ID in descending order (most recent first)
  const sortedAlerts = [...alerts].sort((a, b) => b.id - a.id);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (sortedAlerts.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No historical alerts
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell><strong>Message</strong></TableCell>
            <TableCell><strong>Location</strong></TableCell>
            <TableCell><strong>Created</strong></TableCell>
            <TableCell><strong>Ended</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedAlerts.map((alert) => (
            <TableRow key={alert.id}>
              <TableCell sx={{ whiteSpace: 'pre-line' }}>{alert.message}</TableCell>
              <TableCell>{renderLocation(alert)}</TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body2">{alert.createdBy.name}</Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                    {formatDate(alert.createDate)}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body2">{alert.endedBy?.name || 'Unknown'}</Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                    {alert.endedDate ? formatDate(alert.endedDate) : 'N/A'}
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default HistoricalAlertsTable;
